import {
  type FC,
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { FlowController } from "@/shared/components/FlowController";
import { ONBOARDING_STORAGE_KEYS } from "@/shared/constants/constants";
import type { FlowAnswers } from "@/shared/constants/flowTypes";
import { getHelpTopicsFromAnswers } from "@/shared/constants/helpTopics";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { useAuth } from "@/shared/hooks/useAuth";
import { useAuthRouter } from "@/shared/hooks/useAuthRouter";
import { usePerson, useUser } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import {
  storeOnboardingData,
  promoteAdminIfElayne,
  setAcquisitionSource,
} from "@/shared/utils/onboarding";
import { getGoogleOAuthContext, clearGoogleOAuthContext } from "@/shared/utils/googleOAuth";
import { saveLastAuthMethod } from "@/shared/utils/lastAuthMethod";
import { EventType, trackEvent } from "@/shared/utils/core";
import {
  getDecedentPath,
  getEntryType,
  toErrorType,
  trackOnboardingCompleted,
  trackOnboardingStarted,
  trackUserSignedUp,
  type OnboardingFlow,
} from "@/shared/utils/funnelAnalytics";
import { stripPhone } from "@/shared/utils/formatters";
import { splitName, getFirstName, normalizeName, buildFullName } from "@/shared/utils/names";
import { parseAddress } from "@/shared/utils/addressUtils";
import { notifySignup, notifyProfessionalSignup } from "@/shared/utils/slackNotifications";

import { getVisibleScreenIndex } from "@/shared/utils/onboardingScreens";
import { startObitScrape, resetObitScrapeCache } from "@/shared/utils/obitScrapeCache";
import { useSupabaseFeatureFlags } from "@/shared/hooks/useSupabaseFeatureFlags";
import { sentryUtils } from "@/shared/utils/sentry";

import { preAccountSection, postAccountSection } from "./AccountSetupScreens";

const DEATH_CERT_TASK_ID = "death_certificate";

async function sendWelcomeEmail(authId: string, emailType: "welcome_free" | "welcome_paid") {
  try {
    const { data: userRecord } = await supabase
      .from("users")
      .select("user_id, email, first_name")
      .eq("auth_id", authId)
      .single();

    if (!userRecord?.email || !userRecord?.first_name) return;

    const { data: userPerson } = await supabase
      .from("user_estates")
      .select("estate_id")
      .eq("user_id", userRecord.user_id)
      .limit(1)
      .single();

    let decedentName = "your loved one";
    if (userPerson?.estate_id) {
      const { data: personData } = await supabase
        .from("estates")
        .select("first_name, full_name")
        .eq("estate_id", userPerson.estate_id)
        .single();

      if (personData) {
        const first = personData.first_name || getFirstName(personData.full_name || undefined);
        decedentName = first === "their" ? "your loved one" : first;
      }
    }

    await supabase.functions.invoke("communications/email/send", {
      body: {
        type: emailType,
        to: userRecord.email,
        user_id: userRecord.user_id,
        data: {
          first_name: userRecord.first_name,
          decedent_name: decedentName,
        },
        metadata: {
          onboarding_flow: emailType === "welcome_paid" ? "paid" : "free",
        },
      },
    });
  } catch (error) {
    console.error("Failed to send welcome email:", error);
    sentryUtils.captureError(error, "Failed to send welcome email", undefined, "warning");
  }
}

type FlowStage = "loading" | "pre-account" | "post-account";

export const AccountPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState<FlowStage>("loading");
  const {
    destination,
    isLoading: isCheckingAuth,
    person,
    relationshipToDecedent,
    isInvitedMember: isInvitedFromRouter,
    route,
    isAccountComplete,
    refetch,
  } = useAuthRouter();
  const { flags } = useSupabaseFeatureFlags();
  const emailVerificationEnabled =
    flags.find((f) => f.name === "email_verification_enabled")?.enabled ?? true;
  const contextPerson = usePerson();
  const contextUser = useUser();
  const { user: authUser, loading: isAuthUserLoading } = useAuth();
  // useAuthRouter resolves person before app context is populated; fall back to context for invited members
  const activePerson = person ?? contextPerson;
  const estateId = activePerson?.estate_id;
  const isInvitedMember =
    isInvitedFromRouter ||
    !!(estateId && activePerson && !activePerson.owner_user_id) ||
    !!(
      activePerson?.owner_user_id &&
      contextUser?.user_id &&
      contextUser.user_id !== activePerson.owner_user_id
    );

  const onboardingFlow: OnboardingFlow = isInvitedMember ? "family_invite" : "self_serve";
  const getAnalyticsFlow = useCallback(() => onboardingFlow, [onboardingFlow]);

  const obitParam = searchParams.get("obit");
  const codeParam = searchParams.get("code") === "true";
  const refParam = searchParams.get("utm_source");

  if (obitParam) localStorage.setItem(ONBOARDING_STORAGE_KEYS.OBIT, obitParam);
  if (codeParam) localStorage.setItem(ONBOARDING_STORAGE_KEYS.CODE, "true");
  if (refParam) sessionStorage.setItem(ONBOARDING_STORAGE_KEYS.UTM, refParam);

  const obitUrl = obitParam || localStorage.getItem(ONBOARDING_STORAGE_KEYS.OBIT);
  const hasAccessCode = codeParam || localStorage.getItem(ONBOARDING_STORAGE_KEYS.CODE) === "true";
  const storedUtm = refParam || sessionStorage.getItem(ONBOARDING_STORAGE_KEYS.UTM) || "";

  // Start scraping early so results are ready by the time the user reaches the scrape screen
  if (obitUrl) startObitScrape(obitUrl);

  const [oauthProcessing, setOauthProcessing] = useState(false);

  // Handle Google OAuth return: create user record before normal routing kicks in
  useEffect(() => {
    const oauthContext = getGoogleOAuthContext();
    if (!oauthContext || oauthContext.type !== "onboarding") return;
    if (oauthProcessing) return;

    setOauthProcessing(true);

    const handleOAuthReturn = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          clearGoogleOAuthContext();
          setOauthProcessing(false);
          return;
        }

        // Check if user record already exists
        const { data: existingUser } = await supabase
          .from("users")
          .select("user_id")
          .eq("auth_id", user.id)
          .single();

        if (existingUser) {
          clearGoogleOAuthContext();
          setOauthProcessing(false);
          return;
        }

        const answers = oauthContext.answers ?? {};
        const helpTopics = getHelpTopicsFromAnswers(answers as FlowAnswers);

        const { data: userRecord, error: userError } = await supabase
          .from("users")
          .insert({
            auth_id: user.id,
            email: (user.email ?? "").toLowerCase().trim(),
            first_name: "",
            last_name: "",
            full_name: "",
            phone_number: "",
            completed_onboarding: false,
            completed_authorization: false,
            contact_by_email: true,
            contact_by_phone: false,
            help_topics: helpTopics,
          })
          .select()
          .single();

        if (userError) {
          trackEvent(EventType.SIGNUP_FAILED, {
            flow: "self_serve",
            signup_method: "google",
            error_type: toErrorType(userError),
          });
          console.error("Failed to create user record from Google OAuth:", userError);
          sentryUtils.captureError(
            new Error(`OAuth user record creation failed: ${userError.message}`),
          );
          sentryUtils.logAuthenticationEvent("oauth_signup");
          clearGoogleOAuthContext();
          setOauthProcessing(false);
          return;
        }

        saveLastAuthMethod("google");
        trackUserSignedUp("self_serve", "google");

        promoteAdminIfElayne(userRecord.user_id, user.email ?? "").catch((err) =>
          console.warn("Admin promotion failed (non-blocking):", err),
        );

        if (user.email) {
          setAcquisitionSource(user.email.toLowerCase().trim(), "nok").catch((err) =>
            console.warn("Acquisition source tracking failed (non-blocking):", err),
          );
        }

        clearGoogleOAuthContext();
        setOauthProcessing(false);

        // Re-check auth state so useAuthRouter picks up the new user record
        await refetch();
      } catch (err) {
        trackEvent(EventType.SIGNUP_FAILED, {
          flow: "self_serve",
          signup_method: "google",
          error_type: toErrorType(err),
        });
        console.error("Google OAuth onboarding error:", err);
        sentryUtils.captureError(err, "Google OAuth onboarding error");
        sentryUtils.logAuthenticationEvent("oauth_signup");
        clearGoogleOAuthContext();
        setOauthProcessing(false);
      }
    };

    handleOAuthReturn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return from Stripe checkout success
  useEffect(() => {
    if (searchParams.get("stripe_success") !== "1") return;

    const completeAfterStripe = async () => {
      trackEvent(EventType.CHECKOUT_RETURNED, {
        flow: "self_serve",
        checkout_context: "onboarding_access_code",
        checkout_result: "success",
      });
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: existing } = await supabase
          .from("users")
          .select("completed_onboarding")
          .eq("auth_id", user.id)
          .single();
        await supabase.from("users").update({ completed_onboarding: true }).eq("auth_id", user.id);
        sendWelcomeEmail(user.id, "welcome_paid");
        if (!existing?.completed_onboarding) {
          trackOnboardingCompleted("self_serve", {
            entry_type: "access_code",
            has_paid: true,
          });
        }
      }

      localStorage.removeItem(ONBOARDING_STORAGE_KEYS.CODE);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (obitUrl) {
        setStage("post-account");
        return;
      }

      localStorage.removeItem(ONBOARDING_STORAGE_KEYS.OBIT);
      navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
    };

    completeAfterStripe();
  }, [searchParams, navigate]);

  // Layout effect so this fires before the flow's child effects track the first step view
  useLayoutEffect(() => {
    if (stage === "pre-account" || stage === "post-account") {
      trackOnboardingStarted(onboardingFlow, {
        entry_type: hasAccessCode ? "access_code" : obitUrl ? "obituary_link" : "default",
        utm_source: storedUtm,
        utm_medium: searchParams.get("utm_medium"),
        utm_campaign: searchParams.get("utm_campaign"),
      });
    }
  }, [stage, onboardingFlow, hasAccessCode, obitUrl, storedUtm, searchParams]);

  const obitParamTrackedRef = useRef(false);
  useEffect(() => {
    if (stage === "post-account" && obitUrl && !activePerson && !obitParamTrackedRef.current) {
      obitParamTrackedRef.current = true;
      trackEvent(EventType.DECEDENT_PATH_SELECTED, {
        flow: onboardingFlow,
        decedent_path: "obituary",
        selection_source: "url_param",
      });
    }
  }, [stage, obitUrl, activePerson, onboardingFlow]);

  useEffect(() => {
    if (isCheckingAuth || oauthProcessing) return;
    if (searchParams.get("stripe_success") === "1") return;

    if (destination.type === "dashboard") {
      navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
    } else if (isAccountComplete) {
      navigate(route, { replace: true });
    } else if (destination.type === "login") {
      localStorage.removeItem(ONBOARDING_STORAGE_KEYS.OBIT);
      localStorage.removeItem(ONBOARDING_STORAGE_KEYS.CODE);
      resetObitScrapeCache();
      setStage("pre-account");
    } else if (destination.type === "onboarding") {
      setStage(destination.screen === "about-yourself" ? "pre-account" : "post-account");
    }
  }, [
    isCheckingAuth,
    oauthProcessing,
    searchParams,
    destination,
    isAccountComplete,
    route,
    navigate,
  ]);

  const googleFullName = normalizeName((authUser?.user_metadata?.full_name as string) || "");

  const preAccountInitialAnswers = useMemo((): FlowAnswers => {
    const { firstName, surName } = splitName(googleFullName);
    return {
      "email-verification-enabled": emailVerificationEnabled ? "true" : "false",
      ...(storedUtm ? { "onboarding-utm": storedUtm } : {}),
      ...(isInvitedMember ? { "is-invited-member": "true" } : {}),
      ...(firstName ? { "first-name": firstName } : {}),
      ...(surName ? { "last-name": surName } : {}),
    };
  }, [emailVerificationEnabled, storedUtm, isInvitedMember, googleFullName]);

  const postAccountInitialAnswers = useMemo((): FlowAnswers => {
    const personAnswers: FlowAnswers = {};

    if (activePerson) {
      personAnswers["has-obituary"] = activePerson.obituary_link ? "yes" : "no";
      if (activePerson.obituary_link) {
        personAnswers["obit-link"] = activePerson.obituary_link;
      }
      if (activePerson.full_name) {
        personAnswers["decedent-name"] = activePerson.full_name;
      }
      if (activePerson.date_of_birth) {
        personAnswers["decedent-dob"] = activePerson.date_of_birth;
      }
      if (activePerson.date_of_death) {
        personAnswers["decedent-dod"] = activePerson.date_of_death;
      }
      if (activePerson.location) {
        const parsed = parseAddress(activePerson.location);
        personAnswers["decedent-street-address"] = parsed.streetAddress;
        personAnswers["decedent-apt-unit"] = parsed.apartment;
        personAnswers["decedent-city"] = parsed.city;
        personAnswers["decedent-state"] = parsed.state;
        personAnswers["decedent-zip"] = parsed.zipCode;
      }
    }

    if (relationshipToDecedent) {
      personAnswers["decedent-relationship"] = relationshipToDecedent;
    }

    if (obitUrl) {
      personAnswers["has-obituary"] = "yes";
      personAnswers["obit-link"] = obitUrl;
      personAnswers["obit-from-param"] = "true";
    }

    if (hasAccessCode) {
      personAnswers["entry-type"] = "access-code";
    } else if (obitUrl) {
      personAnswers["entry-type"] = "obituary";
    } else {
      personAnswers["entry-type"] = "default";
    }

    if (isInvitedMember) {
      personAnswers["is-invited-member"] = "true";
    }

    if (contextUser?.stripe_customer_id) {
      personAnswers["has-paid"] = "true";
    }

    if (storedUtm) {
      personAnswers["onboarding-utm"] = storedUtm;
    }

    return personAnswers;
  }, [
    obitUrl,
    hasAccessCode,
    activePerson,
    relationshipToDecedent,
    isInvitedMember,
    contextUser,
    storedUtm,
  ]);

  const isStripeReturn = searchParams.get("stripe_success") === "1";

  const postAccountInitialStep = useMemo(() => {
    if (hasAccessCode && !isStripeReturn) {
      return getVisibleScreenIndex(
        "access-code-redemption",
        postAccountSection.screens,
        postAccountInitialAnswers,
      );
    }
    if (obitUrl && !activePerson) {
      return getVisibleScreenIndex(
        "scrape-loading",
        postAccountSection.screens,
        postAccountInitialAnswers,
      );
    }
    if (destination.type === "onboarding") {
      return getVisibleScreenIndex(
        destination.screen,
        postAccountSection.screens,
        postAccountInitialAnswers,
      );
    }
    return 0;
  }, [hasAccessCode, isStripeReturn, obitUrl, destination, postAccountInitialAnswers]);

  const handlePreAccountStepComplete = useCallback(
    async (
      screenId: string,
      _questionId: string | undefined,
      _answer: string | string[] | undefined,
      allAnswers: FlowAnswers,
    ) => {
      if (screenId !== "about-yourself") return;

      const firstName = normalizeName((allAnswers["first-name"] as string) || "");
      const middleName = normalizeName((allAnswers["middle-name"] as string) || "");
      const lastName = normalizeName((allAnswers["last-name"] as string) || "");
      const phoneNumber = stripPhone((allAnswers["phone-number"] as string) || "");
      const fullName = buildFullName(firstName, middleName, lastName);

      if (!firstName) return;

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userRecord, error: updateError } = await supabase
        .from("users")
        .update({
          first_name: firstName,
          middle_name: middleName || null,
          last_name: lastName,
          full_name: fullName,
          phone_number: phoneNumber,
        })
        .eq("auth_id", authUser.id)
        .select("user_id")
        .single();

      if (updateError || !userRecord) {
        console.error("Failed to update user with name/phone:", updateError);
        return;
      }

      storeOnboardingData({
        userInfo: {
          email: authUser.email ?? "",
          firstName,
          middleName,
          lastName,
          phoneNumber,
        },
      });

      // Non-blocking: phone validation failure doesn't affect signup flow
      if (phoneNumber) {
        supabase.functions
          .invoke("communications/phone/validate", {
            body: { userId: userRecord.user_id, phoneNumber },
          })
          .catch((err: unknown) => console.warn("Phone validation failed (non-blocking):", err));
      }

      notifySignup().catch(console.error);
    },
    [],
  );

  const handlePreAccountComplete = () => {
    setStage("post-account");
  };

  const handlePostAccountStepComplete = useCallback(
    async (screenId: string) => {
      if (screenId !== "upload-death-cert" || !estateId) return;

      const now = new Date().toISOString();
      await supabase
        .from("estate_tasks")
        .update({
          status: "completed",
          started_at: now,
          completed_at: now,
          last_active_at: now,
        })
        .eq("estate_id", estateId)
        .eq("task_id", DEATH_CERT_TASK_ID);
    },
    [estateId],
  );

  const finalizeOnboarding = async (answers: FlowAnswers) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      const { data: existing } = await supabase
        .from("users")
        .select("completed_onboarding, last_active_estate_id")
        .eq("auth_id", user.id)
        .single();
      const alreadyCompleted = !!existing?.completed_onboarding;
      await supabase.from("users").update({ completed_onboarding: true }).eq("auth_id", user.id);
      if (!alreadyCompleted) {
        sendWelcomeEmail(user.id, "welcome_free");
        trackOnboardingCompleted(onboardingFlow, {
          entry_type: getEntryType(answers),
          estate_id: existing?.last_active_estate_id ?? estateId,
          decedent_path: getDecedentPath(answers),
          has_paid: answers["has-paid"] === "true",
        });
      }
      if (answers["decedent-relationship"] === "other-professional") {
        notifyProfessionalSignup().catch(console.error);
      }
    }

    localStorage.removeItem(ONBOARDING_STORAGE_KEYS.OBIT);
    localStorage.removeItem(ONBOARDING_STORAGE_KEYS.CODE);
    localStorage.removeItem(ONBOARDING_STORAGE_KEYS.PERSON_CREATED);
    resetObitScrapeCache();
    await queryClient.invalidateQueries({
      queryKey: ["app-user-data"],
      exact: false,
    });
    navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
  };

  const handlePostAccountComplete = async (answers?: FlowAnswers) => {
    await finalizeOnboarding(answers ?? postAccountInitialAnswers);
  };

  if (stage === "loading" || oauthProcessing || isAuthUserLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (stage === "pre-account") {
    return (
      <FlowController
        key="pre-account"
        section={{
          ...preAccountSection,
          analyticsFlow: getAnalyticsFlow,
          onComplete: handlePreAccountComplete,
          onStepComplete: handlePreAccountStepComplete,
        }}
        initialStep={
          destination.type === "onboarding" && destination.screen === "about-yourself"
            ? getVisibleScreenIndex("about-yourself", preAccountSection.screens, {
                "email-verification-enabled": emailVerificationEnabled ? "true" : "false",
              })
            : 0
        }
        initialAnswers={preAccountInitialAnswers}
      />
    );
  }

  return (
    <FlowController
      key="post-account"
      section={{
        ...postAccountSection,
        analyticsFlow: getAnalyticsFlow,
        onComplete: handlePostAccountComplete,
        onStepComplete: handlePostAccountStepComplete,
      }}
      initialAnswers={postAccountInitialAnswers}
      initialStep={postAccountInitialStep}
    />
  );
};
